import type { ReactElement } from 'react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  MapPin,
  Building2,
  RefreshCw,
  AlertCircle,
  User,
  Store,
  UserCheck,
  UserX,
  TrendingUp,
  X,
  FilterX,
} from 'lucide-react';

import {
  pointsService,
  citiesService,
  chainsService,
} from '../../services/api';
import type {
  City,
  Chain,
  PointOfSale,
  PointFilters,
  RepresentativeOption,
  PointStats,
} from '../../types/api';

import { useToast } from '../../hooks/useToast';
import ToastContainer from '../../components/ui/ToastContainer';
import { StoreModal } from './modals/StoreModal';

interface StoresManagementState {
  stores: PointOfSale[];
  loading: boolean;
  error: string | null;
  totalPages: number;
  currentPage: number;
  total: number;
  searchTerm: string;
  selectedCity: string;
  selectedChain: string;
  selectedRepresentative: string;
  isCreating: boolean;
  isEditing: boolean;
  selectedStore: PointOfSale | null;
  cities: City[];
  chains: Chain[];
  representatives: RepresentativeOption[];
  stats: PointStats | null;
  statsLoading: boolean;
}

const StoresManagement = (): ReactElement => {
  const { addToast, toasts, removeToast } = useToast();

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') ?? 'null') as {
        role?: string;
      } | null;
    } catch {
      return null;
    }
  }, []);

  const canManageStores =
    user?.role === 'SYSTEM_ADMIN' || user?.role === 'ADMIN';

  const [state, setState] = useState<StoresManagementState>({
    stores: [],
    loading: true,
    error: null,
    totalPages: 0,
    currentPage: 1,
    total: 0,
    searchTerm: '',
    selectedCity: '',
    selectedChain: '',
    selectedRepresentative: '',
    isCreating: false,
    isEditing: false,
    selectedStore: null,
    cities: [],
    chains: [],
    representatives: [],
    stats: null,
    statsLoading: true,
  });

  const hasActiveFilters = useMemo(() => {
    return !!(
      state.searchTerm ||
      state.selectedCity ||
      state.selectedChain ||
      state.selectedRepresentative
    );
  }, [
    state.searchTerm,
    state.selectedCity,
    state.selectedChain,
    state.selectedRepresentative,
  ]);

  const loadStores = useCallback(
    async (filters?: Partial<PointFilters>): Promise<void> => {
      if (!canManageStores) return;

      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const queryFilters: PointFilters = {
          page: state.currentPage,
          limit: 10,
          ...(state.searchTerm && { search: state.searchTerm }),
          ...(state.selectedCity && { cityId: state.selectedCity }),
          ...(state.selectedChain && { chainId: state.selectedChain }),
          ...(state.selectedRepresentative && {
            representativeId: state.selectedRepresentative,
          }),
          ...filters,
        };

        const response = await pointsService.getAll(queryFilters);

        setState((prev) => ({
          ...prev,
          stores: response.data || [],
          totalPages: response.totalPages || 0,
          total: response.total || 0,
          loading: false,
        }));
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Error al cargar tiendas';
        addToast('error', message);
        setState((prev) => ({
          ...prev,
          error: message,
          loading: false,
        }));
      }
    },
    [
      canManageStores,
      state.currentPage,
      state.searchTerm,
      state.selectedCity,
      state.selectedChain,
      state.selectedRepresentative,
      addToast,
    ]
  );

  const loadAuxiliaryData = useCallback(async (): Promise<void> => {
    try {
      const [citiesResponse, chainsResponse, representativesData] =
        await Promise.all([
          citiesService.getAll(),
          chainsService.getAll(),
          pointsService.getRepresentatives(),
        ]);

      const parsedCities = Array.isArray(citiesResponse)
        ? citiesResponse
        : citiesResponse?.data || [];

      const parsedChains = Array.isArray(chainsResponse)
        ? chainsResponse
        : chainsResponse?.data || [];

      const parsedRepresentatives = Array.isArray(representativesData)
        ? representativesData
        : representativesData?.data || [];

      setState((prev) => ({
        ...prev,
        cities: parsedCities,
        chains: parsedChains,
        representatives: parsedRepresentatives,
      }));
    } catch (error) {
      console.error('❌ Error al cargar datos auxiliares:', error);
      addToast('error', 'Error al cargar datos auxiliares');
    }
  }, [addToast]);

  const loadStats = useCallback(async (): Promise<void> => {
    try {
      setState((prev) => ({ ...prev, statsLoading: true }));
      const statsData = await pointsService.getStats();
      setState((prev) => ({
        ...prev,
        stats: statsData,
        statsLoading: false,
      }));
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
      setState((prev) => ({ ...prev, statsLoading: false }));
    }
  }, []);

  useEffect(() => {
    if (canManageStores) {
      void loadStores();
      void loadAuxiliaryData();
      void loadStats();
    }
  }, [canManageStores, loadStores, loadAuxiliaryData, loadStats]);

  useEffect(() => {
    if (canManageStores) {
      void loadStores();
    }
  }, [canManageStores, loadStores]);

  const handleSearch = (term: string): void => {
    setState((prev) => ({ ...prev, searchTerm: term, currentPage: 1 }));
  };

  const handlePageChange = (page: number): void => {
    setState((prev) => ({ ...prev, currentPage: page }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCreateStore = async (data: {
    name: string;
    cityId: string;
    chainId: string;
    representativeId?: string | null;
  }): Promise<void> => {
    setState((prev) => ({ ...prev, loading: true }));

    try {
      await pointsService.create(data);
      addToast('success', 'Punto de venta creado correctamente');
      await Promise.all([loadStores(), loadStats()]);
      setState((prev) => ({ ...prev, isCreating: false }));
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Error al crear tienda';
      addToast('error', message);
      setState((prev) => ({
        ...prev,
        error: message,
        loading: false,
      }));
    }
  };

  const handleUpdateStore = async (data: {
    name: string;
    cityId: string;
    chainId: string;
    representativeId?: string | null;
  }): Promise<void> => {
    if (!state.selectedStore) return;

    setState((prev) => ({ ...prev, loading: true }));

    try {
      await pointsService.update(state.selectedStore.id, data);
      addToast('success', 'Punto de venta actualizado correctamente');
      await Promise.all([loadStores(), loadStats()]);
      setState((prev) => ({ ...prev, isEditing: false, selectedStore: null }));
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Error al actualizar tienda';
      addToast('error', message);
      setState((prev) => ({
        ...prev,
        error: message,
        loading: false,
      }));
    }
  };

  const handleDeleteStore = async (id: string): Promise<void> => {
    if (!confirm('¿Estás seguro de que quieres eliminar esta tienda?')) return;

    setState((prev) => ({ ...prev, loading: true }));

    try {
      await pointsService.delete(id);
      addToast('success', 'Punto de venta eliminado correctamente');
      await Promise.all([loadStores(), loadStats()]);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Error al eliminar tienda';
      addToast('error', message);
      setState((prev) => ({
        ...prev,
        error: message,
        loading: false,
      }));
    }
  };

  const handleRefresh = (): void => {
    void loadStores();
    void loadStats();
    void loadAuxiliaryData(); // ✅ Recargar también datos auxiliares
  };

  const handleResetFilters = (): void => {
    setState((prev) => ({
      ...prev,
      searchTerm: '',
      selectedCity: '',
      selectedChain: '',
      selectedRepresentative: '',
      currentPage: 1,
    }));
  };

  const handleOpenCreateModal = (): void => {
    setState((prev) => ({ ...prev, isCreating: true }));
  };

  const getZoneName = (zoneName: string): string => {
    const zoneNames = {
      NORTE: 'Norte',
      CENTRO: 'Centro',
      SUR: 'Sur',
    };
    return zoneNames[zoneName as keyof typeof zoneNames] || zoneName;
  };

  if (!canManageStores) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Acceso Denegado
              </h3>
              <p className="mt-2 text-sm text-red-700">
                No tienes permisos para gestionar tiendas.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Gestión de Puntos de Venta
          </h1>
          <p className="text-gray-600">
            Administra los puntos de venta del sistema
            <span className="ml-2 text-sm">({state.total} total)</span>
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleOpenCreateModal}
            className="flex items-center sm:w-auto px-4 py-2 bg-primary-dark text-white rounded-md hover:bg-primary-accent disabled:opacity-50"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Punto de Venta
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {!state.statsLoading && state.stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-6 rounded-lg shadow border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Total Tiendas
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {state.stats.total}
                </p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <Store className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Con Representante
                </p>
                <p className="text-2xl font-bold text-green-600 mt-1">
                  {state.stats.withRepresentative}
                </p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <UserCheck className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Sin Representante
                </p>
                <p className="text-2xl font-bold text-orange-600 mt-1">
                  {state.stats.withoutRepresentative}
                </p>
              </div>
              <div className="bg-orange-100 p-3 rounded-full">
                <UserX className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">% Cobertura</p>
                <p className="text-2xl font-bold text-primary-dark mt-1">
                  {state.stats.coveragePercentage}%
                </p>
              </div>
              <div className="bg-primary-light p-3 rounded-full">
                <TrendingUp className="h-6 w-6 text-primary-dark" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow border">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Búsqueda */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                value={state.searchTerm}
                onChange={(e) => {
                  handleSearch(e.target.value);
                }}
                placeholder="Buscar por nombre..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent w-full"
              />
            </div>
          </div>

          {/* Ciudad */}
          <div className="relative w-full md:w-48">
            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <select
              value={state.selectedCity}
              onChange={(e) => {
                setState((prev) => ({
                  ...prev,
                  selectedCity: e.target.value,
                  currentPage: 1,
                }));
              }}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent w-full appearance-none"
            >
              <option value="">Todas las ciudades</option>
              {state.cities.map((city) => (
                <option key={city.id} value={city.id}>
                  {city.name}
                </option>
              ))}
            </select>
          </div>

          {/* Cadena */}
          <div className="relative w-full md:w-48">
            <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <select
              value={state.selectedChain}
              onChange={(e) => {
                setState((prev) => ({
                  ...prev,
                  selectedChain: e.target.value,
                  currentPage: 1,
                }));
              }}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent w-full appearance-none"
            >
              <option value="">Todas las cadenas</option>
              {state.chains.map((chain) => (
                <option key={chain.id} value={chain.id}>
                  {chain.name}
                </option>
              ))}
            </select>
          </div>

          {/* Representante */}
          <div className="relative w-full md:w-48">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <select
              value={state.selectedRepresentative}
              onChange={(e) => {
                setState((prev) => ({
                  ...prev,
                  selectedRepresentative: e.target.value,
                  currentPage: 1,
                }));
              }}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent w-full appearance-none"
            >
              <option value="">Todos los representantes</option>
              {state.representatives.map((rep) => (
                <option key={rep.id} value={rep.id}>
                  {rep.user.name}
                </option>
              ))}
            </select>
          </div>

          {/* Botón Refresh - Solo icono */}
          <button
            onClick={handleRefresh}
            disabled={state.loading}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            title="Actualizar"
          >
            <RefreshCw
              className={`h-4 w-4 ${state.loading ? 'animate-spin' : ''}`}
            />
          </button>
        </div>

        {/* Active Filters Badges */}
        {hasActiveFilters && (
          <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-200">
            <span className="text-sm text-gray-600">Filtros activos:</span>

            {state.searchTerm && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                Búsqueda: "{state.searchTerm}"
                <button
                  onClick={() => {
                    setState((prev) => ({
                      ...prev,
                      searchTerm: '',
                      currentPage: 1,
                    }));
                  }}
                  className="hover:bg-blue-200 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}

            {state.selectedCity && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                Ciudad:{' '}
                {state.cities.find((c) => c.id === state.selectedCity)?.name}
                <button
                  onClick={() => {
                    setState((prev) => ({
                      ...prev,
                      selectedCity: '',
                      currentPage: 1,
                    }));
                  }}
                  className="hover:bg-green-200 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}

            {state.selectedChain && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                Cadena:{' '}
                {state.chains.find((c) => c.id === state.selectedChain)?.name}
                <button
                  onClick={() => {
                    setState((prev) => ({
                      ...prev,
                      selectedChain: '',
                      currentPage: 1,
                    }));
                  }}
                  className="hover:bg-purple-200 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}

            {state.selectedRepresentative && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                Representante:{' '}
                {
                  state.representatives.find(
                    (r) => r.id === state.selectedRepresentative
                  )?.user.name
                }
                <button
                  onClick={() => {
                    setState((prev) => ({
                      ...prev,
                      selectedRepresentative: '',
                      currentPage: 1,
                    }));
                  }}
                  className="hover:bg-orange-200 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}

            <button
              onClick={handleResetFilters}
              className="text-xs text-gray-600 hover:text-gray-900 underline ml-2"
            >
              Limpiar todos los filtros
            </button>
          </div>
        )}
      </div>

      {/* Loading State */}
      {state.loading && (
        <div className="flex justify-center items-center py-8">
          <RefreshCw className="h-6 w-6 animate-spin text-green-600" />
          <span className="ml-2 text-gray-600">Cargando tiendas...</span>
        </div>
      )}

      {/* Stores Table */}
      {!state.loading && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tienda
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ubicación
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cadena
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Representante
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {state.stores.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-6 py-8 text-center text-gray-500"
                    >
                      No se encontraron puntos de venta
                    </td>
                  </tr>
                ) : (
                  state.stores.map((store) => (
                    <tr key={store.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Building2 className="h-8 w-8 text-gray-400 mr-3" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {store.name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                          <div>
                            <div className="text-left text-sm text-gray-900">
                              {store.city?.name || 'Sin ciudad'}
                            </div>
                            <div className="text-left text-sm text-gray-500">
                              {getZoneName(store.city?.zone?.name || '')}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {store.chain?.name || 'Sin cadena'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {store.representative ? (
                          <div className="flex items-center">
                            <User className="h-4 w-4 text-gray-400 mr-2" />
                            <div>
                              <div className="text-left text-sm text-gray-900">
                                {store.representative.user?.name ||
                                  'Sin nombre'}
                              </div>
                              <div className="text-sm text-gray-500">
                                #
                                {store.representative.user?.employeeNumber ||
                                  'N/A'}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">
                            Sin asignar
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => {
                              setState((prev) => ({
                                ...prev,
                                isEditing: true,
                                selectedStore: store,
                              }));
                            }}
                            className="text-primary-medium hover:text-primary-dark"
                            title="Editar"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => void handleDeleteStore(store.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Eliminar"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Pagination */}
            {state.totalPages > 1 && (
              <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Mostrando página {state.currentPage} de {state.totalPages} (
                    {state.total} total)
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        handlePageChange(state.currentPage - 1);
                      }}
                      disabled={state.currentPage === 1 || state.loading}
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Anterior
                    </button>

                    <div className="flex items-center gap-1">
                      {Array.from(
                        { length: Math.min(5, state.totalPages) },
                        (_, i) => {
                          let pageNum;
                          if (state.totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (state.currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (
                            state.currentPage >=
                            state.totalPages - 2
                          ) {
                            pageNum = state.totalPages - 4 + i;
                          } else {
                            pageNum = state.currentPage - 2 + i;
                          }

                          return (
                            <button
                              key={pageNum}
                              onClick={() => {
                                handlePageChange(pageNum);
                              }}
                              disabled={state.loading}
                              className={`px-3 py-1 text-sm rounded disabled:opacity-50 ${
                                state.currentPage === pageNum
                                  ? 'bg-primary-accent text-white'
                                  : 'border border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        }
                      )}
                    </div>

                    <button
                      onClick={() => {
                        handlePageChange(state.currentPage + 1);
                      }}
                      disabled={
                        state.currentPage === state.totalPages || state.loading
                      }
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!state.loading && state.stores.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No hay puntos de venta
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Comienza creando tu primer punto de venta.
          </p>
          <div className="mt-6">
            <button
              onClick={handleOpenCreateModal}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-dark hover:bg-primary-accent"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Punto de Venta
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      {state.isCreating && (
        <StoreModal
          isOpen={state.isCreating}
          onClose={() => {
            setState((prev) => ({ ...prev, isCreating: false }));
          }}
          onSave={handleCreateStore}
          cities={state.cities}
          chains={state.chains}
          representatives={state.representatives}
          isLoading={state.loading}
        />
      )}

      {state.isEditing && state.selectedStore && (
        <StoreModal
          isOpen={state.isEditing}
          onClose={() => {
            setState((prev) => ({
              ...prev,
              isEditing: false,
              selectedStore: null,
            }));
          }}
          onSave={handleUpdateStore}
          store={state.selectedStore}
          cities={state.cities}
          chains={state.chains}
          representatives={state.representatives}
          isLoading={state.loading}
        />
      )}
    </div>
  );
};

export default StoresManagement;
